<template>
  <aside class="sidebar" :class="{ 'collapsed': isCollapsed, 'mobile': isMobile }">
    <button
      v-if="props.isMobile"
      class="mobile-close-btn absolute top-4 right-4 z-50"
      @click="emit('toggle-sidebar')"
    >
      <XIcon class="icon" />
    </button>

    <!-- Logo Box - Centered & Resizable -->
    <div v-if="!isMobile" class="logo-container">
      <LogoBox :is-sidebar="true" :class="isCollapsed ? 'collapsed-logo' : 'expanded-logo'" />
    </div>
    <!-- Top Section with Toggle Button & User Info -->
    <div class="sidebar-header">
      <button class="toggle-btn" @click="emit('toggle-sidebar')">
        <ChevronLeft class="icon" v-if="!isCollapsed" />
        <ChevronRight class="icon" v-else />
      </button>


      <div class="user-info inline-flex" v-if="!isCollapsed">
        <img :src="user.image" alt="User Avatar" class="user-avatar rounded-full w-10" />
        <div class="user-details ml-2 font-semibold">
          <h3 class="user-name">{{ user.name }}</h3>
          <p class="user-type text-xs">{{ user.type }}/{{ user.role }}</p>
        </div>
      </div>
    </div>

    <!-- Alerts & Messages -->
    <!--
    <div class="alerts" v-if="!isCollapsed">
      <div class="alert-item" v-for="(alert, index) in alerts" :key="index">
        <Bell class="icon" />
        <span>{{ alert }}</span>
      </div>
      <div class="message-item" v-for="(message, index) in messages" :key="index">
        <MessageSquare class="icon" />
        <span>{{ message }}</span>
      </div>
    </div>
    -->

    <!-- Navigation Links -->
    <nav class="nav-links">
      <router-link
        v-for="link in navLinks"
        :key="link.id"
        :to="link.route"
        class="nav-link"
        active-class="active"
        :class="{
          'border-t border-gray-700': link.divider,
        }"
        @click="onLinkClick"
      >
        <component :is="link.icon" class="icon" />
        <span v-if="!isCollapsed">{{ link.label }}</span>
      </router-link>
    </nav>

    <!-- Sidebar Footer -->
    <div class="sidebar-footer">
      <div v-if="authStore.isAuthenticated"
           class="flex flex-col items-center space-y-4 lg:w-full">

        <!-- Voice Area Link -->

        <router-link to="/"
                     class="flex items-center justify-center w-full bg-anymic-blue text-white py-2 rounded-lg
                 hover:bg-anymic-blue/80 transition-all duration-300 shadow-md">
          <House class="h-5 w-5"/>
          <span v-if="!isCollapsed" class="ms-2">Public Area</span>
        </router-link>

        <!-- Logout Button -->
        <button @click="authStore.logout()"
                class="flex items-center justify-center w-full bg-red-900 text-white py-2 rounded-lg
            hover:bg-red-900/80 transition-all duration-300 shadow-md">
          <LogOutIcon class="h-5 w-5"/>
          <span v-if="!isCollapsed" class="ms-2">Logout</span>
        </button>
      </div>
    </div>

  </aside>
</template>

<script setup lang="ts">
import {type PropType, onMounted, computed} from 'vue';
import { useAuthStore, useUserStore, useProfileStore } from '@/stores';
import { useRouter } from 'vue-router';
// Import icons
import {
  ChevronLeft,
  ChevronRight,
  LogOutIcon,
  House,
  X as XIcon
} from 'lucide-vue-next';
import type {NavBarLinkType} from "@/types/layout";
import LogoBox from "@/components/LogoBox.vue";

const props = defineProps({
  navLinks: {
    type: Array as PropType<NavBarLinkType[]>,
    required: true
  },
  isCollapsed: {
    type: Boolean,
    required: true
  },
  isMobile: {
    type: Boolean,
    required: true,
    //default: false,
  },
})

const emit = defineEmits(['toggle-sidebar']);

const authStore = useAuthStore();
const userStore = useUserStore();
const profileStore = useProfileStore();

const user = computed(() => ({
  organisation: userStore.currentUser?.organisation,
  name: userStore.currentUser?.name,
  role: userStore.currentUser?.role,
  type: userStore.currentUser?.type,
  image: profileStore.currentProfile?.imgUri,
}));

const onLinkClick = () => {
  if (props.isMobile) {
    emit('toggle-sidebar'); // closes sidebar in mobile
  }
};

/*
const alerts = ref(["New event added", "Application deadline approaching"]);
const messages = ref(["New message from HR", "System update scheduled"]);
*/

onMounted(() => {
  //isCollapsed.value = props.isCollapsedOnStart;
});
</script>

<style scoped>
/* ✅ Sidebar Layout */
.sidebar {
  width: 250px;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.6);
  color: white;
  display: flex;
  flex-direction: column;
  padding: 20px;
  transition: width 0.3s ease-in-out;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
}

.sidebar.collapsed {
  width: 80px;
}

.sidebar.mobile {
  background-color: rgba(15, 23, 42, 0.9);
}

/* ✅ Sidebar Header */
.sidebar-header {
  display: flex;
  align-items: center;
  /*
  no space between collapse arrow and user info
  justify-content: space-between;
   */
  margin-bottom: 15px;
}
.toggle-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 5px;
}

/* ✅ Sidebar Navigation */
.nav-links {
  flex-grow: 1;
}
.nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  text-decoration: none;
  color: white;
  border-radius: 8px;
  transition: background 0.3s ease;
}
.nav-link:hover, .nav-link.active {
  background-color: rgba(255, 255, 255, 0.1);
}

/* ✅ Logo Styling */
.logo-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px; /* Adds spacing */
}
.expanded-logo {
  width: 80px; /* Full-size logo */
  transition: width 0.3s ease-in-out;
}

.collapsed-logo {
  width: 50px; /* Smaller logo when collapsed */
  transition: width 0.3s ease-in-out;
}

.mobile-close-btn {
  background: rgba(15, 23, 42, 0.8);
  border: none;
  padding: 8px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
}

</style>
